import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout, Card, Typography, Descriptions, Button, Radio, Modal, message, Alert, Form, Input, InputNumber, Select, Steps } from 'antd';
import { InfoCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

const { Content } = Layout;
const { Title, Text } = Typography;

// 物模型6个步骤
const MODEL_STEPS = [
  { title: '设备能力' },
  { title: '镜头配置' },
  { title: '麦克风配置' },
  { title: '屏显配置' },
  { title: '喇叭配置' },
  { title: '云端抓图' },
];

const FirmwareModelConfig = () => {
  const [searchParams] = useSearchParams();
  const productName = searchParams.get('productName') || '';
  const productId = searchParams.get('productId') || '';
  const fwName = searchParams.get('fwName') || '';
  const fwVersion = searchParams.get('fwVersion') || '';
  const isFinalized = searchParams.get('isFinalized') === '1';
  const urlRole = searchParams.get('role') || 'platform';
  const urlModelSource = searchParams.get('modelSource') || 'inherit';
  const urlSource = searchParams.get('source') || 'self';
  const isEcosystem = urlSource === 'ecosystem';

  const [mockMode, setMockMode] = useState(urlRole === 'enterprise' ? (isFinalized ? 'ent-view' : 'ent-edit') : 'platform');
  // mock：物模型来源，从URL读取；保存后变为自定义
  const [modelSource, setModelSource] = useState(urlModelSource);

  // 只读判定：
  // 1. 企业-已定版 → 只读
  // 2. 方案派生产品 + 固件物模型来源=继承产品（inherit）→ 企业/平台均只读，不可修改
  const derivedInheritReadOnly = isEcosystem && modelSource === 'inherit';
  const readOnly = mockMode === 'ent-view' || derivedInheritReadOnly;

  const [currentStep, setCurrentStep] = useState(0);
  const [saveModalVisible, setSaveModalVisible] = useState(false);

  // 各步骤表单数据
  const [stepFormData, setStepFormData] = useState({
    0: { videoStream: true, ptz: false, nightVision: '红外夜视', wifiBand: '2.4GHz' },
    1: { resolution: '1920×1080', fps: 25, bitrate: '2048', encoding: 'H.264', streamCount: 2 },
    2: { samplingRate: '16kHz', encoding: 'AAC', noiseReduction: true, sensitivity: '中' },
    3: { displayType: 'OLED', resolution: '128×64', brightness: 5 },
    4: { speakerType: '单声道', power: 2, volumeMax: 100, supported: true },
    5: { captureMode: '定时抓图', interval: 30, quality: '高清', uploadPath: '/default' },
  });

  const handleStepDataChange = (field, value) => {
    setStepFormData(prev => ({
      ...prev,
      [currentStep]: { ...prev[currentStep], [field]: value },
    }));
  };

  const handleSaveClick = () => {
    setSaveModalVisible(true);
  };

  const handleSaveConfirm = () => {
    setSaveModalVisible(false);
    if (modelSource === 'inherit') {
      setModelSource('custom');
    }
    message.success('物模型配置已保存');
  };

  // 渲染当前步骤内容
  const renderStepContent = () => {
    const data = stepFormData[currentStep] || {};

    switch (currentStep) {
      case 0: return (
        <Form layout="vertical" disabled={readOnly}>
          <Form.Item label="视频流">
            <Select value={data.videoStream} onChange={v => handleStepDataChange('videoStream', v)}
              options={[{ value: true, label: '支持' }, { value: false, label: '不支持' }]} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="云台控制">
            <Select value={data.ptz} onChange={v => handleStepDataChange('ptz', v)}
              options={[{ value: true, label: '支持' }, { value: false, label: '不支持' }]} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="夜视模式">
            <Select value={data.nightVision} onChange={v => handleStepDataChange('nightVision', v)}
              options={['红外夜视', '全彩夜视', '智能双光'].map(v => ({ value: v, label: v }))} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="Wi-Fi频段">
            <Select value={data.wifiBand} onChange={v => handleStepDataChange('wifiBand', v)}
              options={['2.4GHz', '5GHz', '双频'].map(v => ({ value: v, label: v }))} style={{ width: 200 }} />
          </Form.Item>
        </Form>
      );

      case 1: return (
        <Form layout="vertical" disabled={readOnly}>
          <Form.Item label="分辨率">
            <Select value={data.resolution} onChange={v => handleStepDataChange('resolution', v)}
              options={['1920×1080', '2560×1440', '3840×2160', '640×480'].map(v => ({ value: v, label: v }))} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="帧率 (fps)">
            <InputNumber min={1} max={60} value={data.fps} onChange={v => handleStepDataChange('fps', v)} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="码率 (kbps)">
            <Select value={data.bitrate} onChange={v => handleStepDataChange('bitrate', v)}
              options={['512', '1024', '2048', '4096'].map(v => ({ value: v, label: v }))} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="编码格式">
            <Select value={data.encoding} onChange={v => handleStepDataChange('encoding', v)}
              options={['H.264', 'H.265', 'MJPEG'].map(v => ({ value: v, label: v }))} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="码流数">
            <InputNumber min={1} max={3} value={data.streamCount} onChange={v => handleStepDataChange('streamCount', v)} style={{ width: 200 }} />
          </Form.Item>
        </Form>
      );

      case 2: return (
        <Form layout="vertical" disabled={readOnly}>
          <Form.Item label="采样率">
            <Select value={data.samplingRate} onChange={v => handleStepDataChange('samplingRate', v)}
              options={['8kHz', '16kHz', '44.1kHz', '48kHz'].map(v => ({ value: v, label: v }))} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="编码格式">
            <Select value={data.encoding} onChange={v => handleStepDataChange('encoding', v)}
              options={['AAC', 'PCM', 'G.711'].map(v => ({ value: v, label: v }))} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="降噪">
            <Select value={data.noiseReduction} onChange={v => handleStepDataChange('noiseReduction', v)}
              options={[{ value: true, label: '启用' }, { value: false, label: '不启用' }]} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="灵敏度">
            <Select value={data.sensitivity} onChange={v => handleStepDataChange('sensitivity', v)}
              options={['低', '中', '高'].map(v => ({ value: v, label: v }))} style={{ width: 200 }} />
          </Form.Item>
        </Form>
      );

      case 3: return (
        <Form layout="vertical" disabled={readOnly}>
          <Form.Item label="屏幕类型">
            <Select value={data.displayType} onChange={v => handleStepDataChange('displayType', v)}
              options={['OLED', 'LCD', 'TFT', '无屏幕'].map(v => ({ value: v, label: v }))} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="分辨率">
            <Select value={data.resolution} onChange={v => handleStepDataChange('resolution', v)}
              options={['128×64', '240×240', '320×240'].map(v => ({ value: v, label: v }))} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="亮度等级 (1-10)">
            <InputNumber min={1} max={10} value={data.brightness} onChange={v => handleStepDataChange('brightness', v)} style={{ width: 200 }} />
          </Form.Item>
        </Form>
      );

      case 4: return (
        <Form layout="vertical" disabled={readOnly}>
          <Form.Item label="喇叭类型">
            <Select value={data.speakerType} onChange={v => handleStepDataChange('speakerType', v)}
              options={['单声道', '双声道', '立体声'].map(v => ({ value: v, label: v }))} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="功率 (W)">
            <InputNumber min={0.5} max={10} step={0.5} value={data.power} onChange={v => handleStepDataChange('power', v)} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="最大音量">
            <InputNumber min={1} max={200} value={data.volumeMax} onChange={v => handleStepDataChange('volumeMax', v)} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="是否支持">
            <Select value={data.supported} onChange={v => handleStepDataChange('supported', v)}
              options={[{ value: true, label: '支持' }, { value: false, label: '不支持' }]} style={{ width: 200 }} />
          </Form.Item>
        </Form>
      );

      case 5: return (
        <Form layout="vertical" disabled={readOnly}>
          <Form.Item label="抓图方式">
            <Select value={data.captureMode} onChange={v => handleStepDataChange('captureMode', v)}
              options={['定时抓图', '事件抓图', '手动抓图'].map(v => ({ value: v, label: v }))} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="抓图间隔 (秒)">
            <InputNumber min={5} max={3600} value={data.interval} onChange={v => handleStepDataChange('interval', v)} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="图片质量">
            <Select value={data.quality} onChange={v => handleStepDataChange('quality', v)}
              options={['标清', '高清', '原图'].map(v => ({ value: v, label: v }))} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="上传路径">
            <Input value={data.uploadPath} onChange={e => handleStepDataChange('uploadPath', e.target.value)} style={{ width: 300 }} />
          </Form.Item>
        </Form>
      );

      default: return <Text type="secondary">暂无内容</Text>;
    }
  };

  return (
    <Layout style={{ padding: 20, background: '#fff' }}>
      <Content>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>物模型配置</Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text type="secondary">Mock角色：</Text>
            <Radio.Group value={mockMode} onChange={e => setMockMode(e.target.value)} optionType="button" buttonStyle="solid" size="small">
              <Radio.Button value="ent-edit">企业-未定版</Radio.Button>
              <Radio.Button value="ent-view">企业-已定版</Radio.Button>
              <Radio.Button value="platform">平台角色</Radio.Button>
            </Radio.Group>
          </div>
        </div>

        <Card bordered={false} style={{ marginBottom: 16, borderRadius: 8 }}>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="产品名称">{productName}</Descriptions.Item>
            <Descriptions.Item label="产品ID">{productId}</Descriptions.Item>
            <Descriptions.Item label="固件名称">{fwName}</Descriptions.Item>
            <Descriptions.Item label="固件版本">{fwVersion}</Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 物模型来源提示 */}
        {modelSource === 'inherit' ? (
          <Alert
            type={derivedInheritReadOnly ? 'warning' : 'info'}
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16, borderRadius: 8, background: derivedInheritReadOnly ? '#fff7e6' : '#fafafa', border: derivedInheritReadOnly ? '1px solid #ffd591' : '1px solid #e8e8e8' }}
            message={<Text strong style={{ color: derivedInheritReadOnly ? '#fa8c16' : '#8c8c8c' }}>继承产品</Text>}
            description={
              <div>
                <div>该固件当前跟随产品物模型，产品变更后自动生效。</div>
                {derivedInheritReadOnly ? (
                  <div style={{ marginTop: 4 }}>本产品为方案派生，固件物模型继承自方案产品，企业及平台均不可修改。</div>
                ) : (
                  !readOnly && <div style={{ marginTop: 4 }}>保存固件级配置后，将脱离产品继承，不再跟随变更。</div>
                )}
              </div>
            }
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: 16, borderRadius: 8, background: '#fff2f0', border: '1px solid #ffccc7' }}
            message={<Text strong style={{ color: '#ff4d4f' }}>固件定义</Text>}
            description={
              <div>
                <div>该固件已有独立物模型配置，不再跟随产品变更。</div>
                {!readOnly && <div style={{ marginTop: 4 }}>修改后保存将更新当前独立配置。</div>}
              </div>
            }
          />
        )}

        {/* 保存按钮 + 步骤指示器 */}
        {!readOnly && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <Button type="primary" style={{ background: '#52c41a', borderColor: '#52c41a' }} onClick={handleSaveClick}>保存全部配置</Button>
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <Steps current={currentStep} size="small" onChange={setCurrentStep}
            items={MODEL_STEPS.map((step, i) => ({ title: i === currentStep ? <Text strong>{step.title}</Text> : step.title }))} />
        </div>

        {/* 当前步骤内容 */}
        <Card bordered={false} style={{ borderRadius: 8, marginBottom: 16, minHeight: 280 }}
          title={<Text strong>{MODEL_STEPS[currentStep].title}</Text>}>
          {renderStepContent()}
        </Card>

        {/* 保存确认弹窗 */}
        <Modal
          title="确认保存"
          open={saveModalVisible}
          onOk={handleSaveConfirm}
          onCancel={() => setSaveModalVisible(false)}
          okText="确认保存"
          cancelText="取消"
        >
          <div style={{ padding: '12px 0' }}>
            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 22, marginRight: 8 }} />
            <Text strong style={{ fontSize: 14 }}>
              物模型变更后固件物模型将被单独定义，不再继承产品。
            </Text>
          </div>
        </Modal>
      </Content>
    </Layout>
  );
};

export default FirmwareModelConfig;
